import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import CrudModal from "../../components/CrudModal";
import { toast } from "sonner";
import { Fingerprint, Camera, WifiHigh, MapPin, CalendarBlank, PencilSimple, Trash, Plus } from "@phosphor-icons/react";

const FIELDS = [
  { name: "course_code", label: "Course code", type: "text" },
  { name: "course_title", label: "Course title", type: "text" },
  { name: "faculty", label: "Faculty", type: "text" },
  { name: "date", label: "Date (YYYY-MM-DD)", type: "text" },
  { name: "present", label: "Present", type: "number" },
  { name: "absent", label: "Absent", type: "number" },
  { name: "late", label: "Late (>15 min)", type: "number" },
];

export default function Attendance() {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [modal, setModal] = useState({ open: false, editing: null });

  const load = useCallback(async () => {
    const { data } = await api.get("/attendance/sessions");
    setSessions(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => setModal({ open: true, editing: null });
  const openEdit = (s) => setModal({ open: true, editing: s });
  const close = () => setModal({ open: false, editing: null });

  const save = async (values) => {
    try {
      if (modal.editing) {
        await api.patch(`/attendance/sessions/${modal.editing.session_id}`, values);
        toast.success("Session updated");
      } else {
        await api.post("/attendance/sessions", values);
        toast.success("Attendance marked");
      }
      close(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete attendance for ${s.course_code} on ${s.date}?`)) return;
    try { await api.delete(`/attendance/sessions/${s.session_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const totalPresent = sessions.reduce((a, s) => a + s.present, 0);
  const totalAbsent = sessions.reduce((a, s) => a + s.absent, 0);
  const totalLate = sessions.reduce((a, s) => a + s.late, 0);
  const total = totalPresent + totalAbsent + totalLate;
  const overallRate = total ? ((totalPresent / total) * 100).toFixed(1) : "0";

  const modes = [
    { I: Fingerprint, l: "Biometric", v: "1,842", t: "fingerprint scanners" },
    { I: Camera, l: "Facial", v: "2,104", t: "AI face match" },
    { I: WifiHigh, l: "RFID", v: "412", t: "smart cards" },
    { I: MapPin, l: "Geofenced", v: "34", t: "field-trip check-in" },
  ];

  return (
    <div>
      <PageHeader
        overline="M07 · Academic Spine"
        title={t("modules.attendance.title")}
        description={t("modules.attendance.subtitle")}
        action={
          <Button onClick={openCreate} className="rounded-md bg-[hsl(20_90%_48%)] hover:bg-[hsl(20_90%_42%)]" data-testid="mark-attendance-btn">
            <Plus size={14} className="mr-1.5"/> Mark attendance
          </Button>
        }
      />
      <div className="p-6 lg:p-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-md overflow-hidden">
          <div className="bg-white p-5">
            <div className="eos-overline">Total Present</div>
            <div className="font-heading font-extrabold text-3xl mt-2 tracking-tight">{totalPresent.toLocaleString()}</div>
            <div className="text-[11px] text-muted-foreground mt-1">{overallRate}% attendance rate</div>
          </div>
          <div className="bg-white p-5">
            <div className="eos-overline">Absent</div>
            <div className="font-heading font-extrabold text-3xl mt-2 tracking-tight text-rose-700">{totalAbsent}</div>
            <div className="text-[11px] text-muted-foreground mt-1">SMS sent to parents</div>
          </div>
          <div className="bg-white p-5">
            <div className="eos-overline">Late</div>
            <div className="font-heading font-extrabold text-3xl mt-2 tracking-tight text-amber-600">{totalLate}</div>
            <div className="text-[11px] text-muted-foreground mt-1">&gt; 15 min</div>
          </div>
          <div className="bg-white p-5">
            <div className="eos-overline">Sessions logged</div>
            <div className="font-heading font-extrabold text-3xl mt-2 tracking-tight">{sessions.length}</div>
            <div className="text-[11px] text-muted-foreground mt-1">all modes combined</div>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-5">
          <div className="lg:col-span-5 eos-card">
            <div className="eos-overline">Capture Modes</div>
            <div className="font-heading font-semibold text-base mt-1">All channels active</div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {modes.map(m => (
                <div key={m.l} className="border border-border rounded-md p-3">
                  <m.I size={18} weight="duotone" className="text-[hsl(244_53%_20%)]"/>
                  <div className="text-xs text-muted-foreground mt-2">{m.l}</div>
                  <div className="font-heading font-bold text-xl">{m.v}</div>
                  <div className="text-[10px] text-muted-foreground">{m.t}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 eos-card p-0 overflow-hidden">
            <div className="p-4 border-b border-border flex items-center gap-2">
              <CalendarBlank size={16} weight="duotone"/>
              <div className="font-heading font-semibold text-base">Recent attendance sessions</div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-secondary text-muted-foreground">
                <tr>{["Date", "Course", "Faculty", "P/A/L", "Rate", "Actions"].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] font-medium">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {sessions.map(s => (
                  <tr key={s.session_id} className="border-t border-border hover:bg-secondary/40" data-testid={`att-row-${s.session_id}`}>
                    <td className="px-4 py-3 font-mono-eos text-[12px]">{s.date}</td>
                    <td className="px-4 py-3 font-medium">{s.course_code} · {s.course_title}</td>
                    <td className="px-4 py-3 text-muted-foreground">{s.faculty}</td>
                    <td className="px-4 py-3 font-mono-eos text-xs">{s.present}/{s.absent}/{s.late}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 w-28">
                        <div className="h-1.5 flex-1 bg-border rounded-full overflow-hidden">
                          <div className={`h-full ${s.rate >= 90 ? "bg-emerald-500" : s.rate >= 75 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${s.rate}%` }}/>
                        </div>
                        <span className="font-mono-eos text-xs">{s.rate}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" onClick={() => openEdit(s)} className="h-7 w-7 p-0 rounded" title="Edit" data-testid={`edit-${s.session_id}`}><PencilSimple size={13}/></Button>
                        <Button variant="ghost" onClick={() => remove(s)} className="h-7 w-7 p-0 rounded text-rose-600 hover:text-rose-700 hover:bg-rose-50" title="Delete" data-testid={`delete-${s.session_id}`}><Trash size={13}/></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <CrudModal
        open={modal.open}
        onClose={close}
        title={modal.editing ? "Edit attendance" : "Mark new attendance"}
        fields={FIELDS}
        initial={modal.editing || { present: 0, absent: 0, late: 0, date: new Date().toISOString().slice(0, 10) }}
        onSubmit={save}
        submitLabel={modal.editing ? "Save" : "Mark attendance"}
      />
    </div>
  );
}
